import React from 'react';
import {
  addCustomTableCol,
  addCustomTableRow,
  MAX_COLS,
  MAX_ROWS,
  MIN_COLS,
  MIN_ROWS,
  removeCustomTableCol,
  removeCustomTableRow,
  resizeCustomTable,
  updateCustomTableCell,
} from '../helpers/pdfCustomTableUtils';
import PdfLayoutJoystick from './PdfLayoutJoystick';

const inputClass = 'w-full border border-gray-300 rounded px-1.5 py-1 text-xs min-w-0';
const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';

function PdfCustomTableEditor({ table, index, onChange, onRemove }) {
  const setTable = (next) => onChange(next);

  const setSize = (rows, cols) => {
    setTable(resizeCustomTable(table, rows, cols));
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-xs font-semibold text-gray-500">Table {index + 1}</span>
        <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:underline">
          Remove table
        </button>
      </div>

      <div>
        <label className={labelClass}>Section title (on PDF)</label>
        <input
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          value={table.title || ''}
          placeholder="e.g. Shipping details"
          onChange={(e) => setTable({ ...table, title: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelClass}>Rows</label>
          <input
            type="number"
            min={MIN_ROWS}
            max={MAX_ROWS}
            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
            value={table.rows}
            onChange={(e) => setSize(Number(e.target.value) || MIN_ROWS, table.cols)}
          />
        </div>
        <div>
          <label className={labelClass}>Columns</label>
          <input
            type="number"
            min={MIN_COLS}
            max={MAX_COLS}
            className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
            value={table.cols}
            onChange={(e) => setSize(table.rows, Number(e.target.value) || MIN_COLS)}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 pb-1">
          <input
            type="checkbox"
            checked={table.headerRow !== false}
            onChange={(e) => setTable({ ...table, headerRow: e.target.checked })}
          />
          First row is header
        </label>
      </div>

      <PdfLayoutJoystick
        sectionLabel="This table on PDF"
        offset={table.layout || { x: 0, y: 0, w: 0, h: 0 }}
        onNudge={(dx, dy) => {
          const layout = table.layout || { x: 0, y: 0, w: 0, h: 0 };
          setTable({ ...table, layout: { ...layout, x: layout.x + dx, y: layout.y + dy } });
        }}
        onReset={() => setTable({ ...table, layout: { x: 0, y: 0, w: 0, h: 0 } })}
        enableSize
        onResize={(dw, dh) => {
          const layout = table.layout || { x: 0, y: 0, w: 0, h: 0 };
          setTable({ ...table, layout: { ...layout, w: layout.w + dw, h: layout.h + dh } });
        }}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={table.rows >= MAX_ROWS}
          onClick={() => setTable(addCustomTableRow(table))}
          className="text-xs text-indigo-600 hover:underline disabled:opacity-40"
        >
          + Row
        </button>
        <button
          type="button"
          disabled={table.cols >= MAX_COLS}
          onClick={() => setTable(addCustomTableCol(table))}
          className="text-xs text-indigo-600 hover:underline disabled:opacity-40"
        >
          + Column
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[200px]">
          <tbody>
            {table.cells.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex} className="border border-gray-200 p-0.5 align-top">
                    <input
                      className={`${inputClass} ${table.headerRow && rowIndex === 0 ? 'font-semibold bg-gray-50' : ''}`}
                      value={cell}
                      placeholder={`R${rowIndex + 1}C${colIndex + 1}`}
                      onChange={(e) => setTable(updateCustomTableCell(table, rowIndex, colIndex, e.target.value))}
                    />
                  </td>
                ))}
                <td className="p-0.5 w-8 align-middle">
                  {table.rows > MIN_ROWS && (
                    <button
                      type="button"
                      title="Remove row"
                      onClick={() => setTable(removeCustomTableRow(table, rowIndex))}
                      className="text-gray-400 hover:text-red-600 text-xs px-1"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {table.cols > MIN_COLS && (
            <tfoot>
              <tr>
                {table.cells[0]?.map((_, colIndex) => (
                  <td key={colIndex} className="text-center pt-1">
                    <button
                      type="button"
                      title="Remove column"
                      onClick={() => setTable(removeCustomTableCol(table, colIndex))}
                      className="text-gray-400 hover:text-red-600 text-xs"
                    >
                      × col
                    </button>
                  </td>
                ))}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default PdfCustomTableEditor;
